/** Must match `$sd-offset-y` in SelectionDropdown.sass */
export const DROPDOWN_GAP = 4;

export type DropdownPlacement = 'below' | 'above';

const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * Opens the panel above its anchor when it would not fit below inside the nearest scroll
 * container (clamped to the viewport) and there is more room above; otherwise below (the default).
 */
export const resolveDropdownPlacement = ({
  anchor,
  panel,
  gap = DROPDOWN_GAP,
}: {
  anchor: HTMLElement;
  panel: HTMLElement;
  gap?: number;
}): DropdownPlacement => {
  const anchorRect = anchor.getBoundingClientRect();
  const needed = panel.getBoundingClientRect().height + gap;
  const bounds = getScrollParent(anchor)?.getBoundingClientRect();
  const visibleTop = Math.max(bounds ? bounds.top : 0, 0);
  const visibleBottom = Math.min(bounds ? bounds.bottom : window.innerHeight, window.innerHeight);
  const spaceBelow = visibleBottom - anchorRect.bottom;
  const spaceAbove = anchorRect.top - visibleTop;
  return spaceBelow < needed && spaceAbove > spaceBelow ? 'above' : 'below';
};

export const DROPDOWN_VIEWPORT_MARGIN = 16;

export const resolveDropdownOffsetX = ({
  anchor,
  panel,
  margin = DROPDOWN_VIEWPORT_MARGIN,
}: {
  anchor: HTMLElement;
  panel: HTMLElement;
  margin?: number;
}): number => {
  const anchorRect = anchor.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  const panelWidth = panelRect.width;
  const centred = anchorRect.left + anchorRect.width / 2 - panelWidth / 2;
  const bounds = getScrollParent(anchor)?.getBoundingClientRect();
  const visibleLeft = Math.max(bounds ? bounds.left : 0, 0);
  const visibleRight = Math.min(bounds ? bounds.right : window.innerWidth, window.innerWidth);
  const nearestLeft = visibleLeft + margin;
  const furthestLeft = Math.max(nearestLeft, visibleRight - margin - panelWidth);
  const clamped = Math.min(Math.max(centred, nearestLeft), furthestLeft);
  return Math.round(panel.offsetLeft + (clamped - panelRect.left));
};

export const DROPDOWN_MAX_HEIGHT = 268;

export const resolveDropdownMaxHeight = ({
  anchor,
  placement,
  gap = DROPDOWN_GAP,
  limit = DROPDOWN_MAX_HEIGHT,
}: {
  anchor: HTMLElement;
  placement: DropdownPlacement;
  gap?: number;
  limit?: number;
}): number => {
  const anchorRect = anchor.getBoundingClientRect();
  const bounds = getScrollParent(anchor)?.getBoundingClientRect();
  const visibleTop = Math.max(bounds ? bounds.top : 0, 0);
  const visibleBottom = Math.min(bounds ? bounds.bottom : window.innerHeight, window.innerHeight);
  const available =
    placement === 'above'
      ? anchorRect.top - visibleTop - gap
      : visibleBottom - anchorRect.bottom - gap;
  return Math.max(0, Math.min(limit, Math.round(available)));
};
